import React, { DragEvent, useCallback, useMemo } from "react";
import getUserName from "../../utils/staffmanagement";
import { StaffManagementAreas, StaffManagementBays, StaffMtpEmployee } from "../../types/staffManagement";
import { User } from "../../types/user";
import { CheckCircleRounded, Delete, HighlightOffRounded, NightlightRound, ScheduleOutlined } from '../../components/icons';
import { ProfileImg } from "../../components";
import { Typography, IconButton, Avatar, Chip, Badge, Tooltip } from "../../tailwindcomponents";
import { v4 as uuid } from 'uuid';
import { Build as BuildIcon } from '../../components/icons';
interface Props {
    employee: StaffMtpEmployee | User;
    bay: StaffManagementBays;
    group: StaffManagementAreas | undefined;
    empMappedKey: number;
    handleDragStart: (employee: User | StaffMtpEmployee, bay: StaffManagementBays | undefined, group: StaffManagementAreas | undefined, empMappedKey: number, areaIndex: number) => void;
    setDragging: Function;
    handleDragOver: (e: DragEvent<HTMLDivElement>) => void;
    handleAboutEmployeeDialog: Function;
    deleteEmployeeFromGroup: (employee: StaffMtpEmployee, group: StaffManagementAreas) => void;
    isStmAdmin: boolean;
}

const EmpCard = ({ employee, bay, group, empMappedKey, handleDragStart, setDragging, handleDragOver, handleAboutEmployeeDialog, deleteEmployeeFromGroup, isStmAdmin }: Props) => {
    
    const getEmployeeAvatarUrl = useCallback((employee: StaffMtpEmployee | User): string => {
        let userName2 = "";
        if (employee !== null) {
            userName2 = getUserName(employee);
        }
        return userName2;
    }, [employee]);

    const empGridId: string = useMemo(() => uuid(), []);
    const employeeAvatarUrl = useMemo(() => getEmployeeAvatarUrl(employee), [getEmployeeAvatarUrl]);
    
    const attendanceIcon = (employeeStatus: string) => {
        let code = <div></div>;
        switch (employeeStatus) {
            case ("Away"):
                code = <Avatar style={{ backgroundColor: 'orange', width: 15, height: 15 }}>
                    <ScheduleOutlined size={12} />
                </Avatar>
                break;
            case ("Night Shift"):
                code = <Avatar style={{ backgroundColor: 'blue', width: 15, height: 15 }}>
                    <NightlightRound size={12} />
                </Avatar>
                break;
            case ("Vacation"):
                code = <Avatar style={{ backgroundColor: 'pink', width: 15, height: 15 }}>
                    <HighlightOffRounded size={12} />
                </Avatar>
                break;
            default:
                code = <Avatar style={{ backgroundColor: 'green', width: 15, height: 15 }}>
                    <CheckCircleRounded size={12} />
                </Avatar>
        }
        return code;
    }
    const isLeader = () => {
        if ('is_team_leader' in employee) {
            return employee.is_team_leader;
        }
        return false;
    }

    const isStaffEmployee = ( employee: StaffMtpEmployee | User): employee is StaffMtpEmployee => {
        return 'hasToolLoanedOut' in employee;
    };

    const ToolStatusIcon = ({ employee }: { employee: StaffMtpEmployee | User }) => {
        if (!isStaffEmployee(employee)) return null;
        if (!employee.hasToolLoanedOut) return null;

        return (
            <div className="mt-2">
                <Tooltip title={employee.toolDescription ? employee.toolDescription : 'Unknown tool'} placement="bottom">
                    <BuildIcon
                        color={employee.isToolsLoanedOutToLong ? 'error' : 'success'}
                    />
                </Tooltip>
            </div>
        );
    };

    return (
        <div
            id={empGridId}
            draggable={isStmAdmin}
            onDragStart={() => { handleDragStart(employee, bay, group, empMappedKey, bay.areas.findIndex((area) => area.area === group?.area)); setDragging(true); }}
            onDragOver={(e: DragEvent<HTMLDivElement>) => handleDragOver(e)}
            className="w-full bg-background-paper rounded-[var(--ds-r-sm)] border border-solid border-line m-1 flex items-center"
            key={empGridId}
        >
            <div className="flex flex-wrap w-full items-center">
                {/* Profile Image Section */}
                <div className="flex flex-wrap w-1/6 justify-start" onClick={() => handleAboutEmployeeDialog(true, employee)}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={attendanceIcon(employee.employee_attendance_status)}
                    >
                        <ProfileImg key={empGridId} imgKey={empGridId} userName={employeeAvatarUrl} />
                    </Badge>
                </div>

                {/* Employee Name Section */}
                <div className="flex flex-wrap w-1/2" onClick={() => handleAboutEmployeeDialog(true, employee)}>
                    <Typography
                        customFontSize={14}
                        variant="body1"
                        text={`${employee.employee_name} ${employee.employee_abbreviation || ''}`}
                    />
                </div>

                {/* Lead/SS Section */}
                {isLeader() && (
                    <div className="flex flex-wrap w-1/6 items-center" >
                        <Chip  size="small" label="SS/Lead" />
                    </div>
                )}

                {/* Tool icon and Delete Button Section */}
                <div
                    className="flex flex-wrap w-1/6 justify-end ml-auto"
                >
                    <ToolStatusIcon employee={employee} />
                    {group !== undefined && isStmAdmin && (
                        <IconButton onClick={() => deleteEmployeeFromGroup(employee as StaffMtpEmployee, group as StaffManagementAreas)}>
                            <Delete color="error" />
                        </IconButton>
                    )}
                </div>
            </div>
        </div>
    )
}

export default React.memo(EmpCard);